import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getDrugGroups,
  createDrugGroup,
  getDrugGroup,
  updateDrugGroup,
  deleteDrugGroup,
} from '@/features/internal/drugs/apis/soapTemplate'
import {
  DrugGroup,
  DrugGroupDetail,
  PaginatedResponse,
  CreateDrugGroupRequest,
  UpdateDrugGroupRequest,
} from '@/features/internal/drugs/types/soapTemplate'
import { useToast } from '@/shared/contexts/ToastContext'

interface UseDrugGroupsQueryOptions {
  page?: number
  search?: string
  ordering?: string
  enabled?: boolean
}

/**
 * 医薬品グループ一覧取得クエリ
 *
 * @param options.page - ページ番号
 * @param options.search - 検索キーワード
 * @param options.ordering - ソート順（デフォルト: 'name'）
 * @param options.enabled - クエリを有効にするか
 */
export const useDrugGroupsQuery = (options: UseDrugGroupsQueryOptions = {}) => {
  const { page = 1, search = '', ordering = 'name', enabled = true } = options

  const query = useQuery({
    queryKey: [...queryKeys.drugGroups.all, 'list', { page, search, ordering }],
    queryFn: async (): Promise<PaginatedResponse<DrugGroup>> => {
      const response = await getDrugGroups(page, search, ordering)
      return response.data
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return query
}

/**
 * 医薬品グループ詳細取得クエリ
 *
 * @param groupId - グループID（'create' の場合はスキップ）
 */
export const useDrugGroupDetailQuery = (groupId: string | undefined) => {
  const query = useQuery({
    queryKey: queryKeys.drugGroups.detail(groupId ?? ''),
    queryFn: async (): Promise<DrugGroupDetail | null> => {
      if (!groupId || groupId === 'create') {
        return null
      }
      const response = await getDrugGroup(groupId)
      return response.data
    },
    enabled: !!groupId && groupId !== 'create',
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return query
}

/**
 * 医薬品グループ作成 Mutation
 */
export const useCreateDrugGroupMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (data: CreateDrugGroupRequest) => {
      const response = await createDrugGroup(data)
      return response.data
    },
    onSuccess: () => {
      showToast('医薬品グループを作成しました', 'success')
      // 一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: queryKeys.drugGroups.all,
      })
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const errorMessage =
        error.response?.data?.detail || '医薬品グループの作成に失敗しました'
      showToast(errorMessage, 'error')
    },
  })
}

/**
 * 医薬品グループ更新 Mutation
 */
export const useUpdateDrugGroupMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      groupId,
      data,
    }: {
      groupId: string
      data: UpdateDrugGroupRequest
    }) => {
      const response = await updateDrugGroup(groupId, data)
      return response.data
    },
    onSuccess: (data, { groupId }) => {
      showToast('医薬品グループを更新しました', 'success')
      // 詳細キャッシュを更新
      queryClient.setQueryData(queryKeys.drugGroups.detail(groupId), data)
      // 一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: queryKeys.drugGroups.all,
      })
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const errorMessage =
        error.response?.data?.detail || '医薬品グループの更新に失敗しました'
      showToast(errorMessage, 'error')
    },
  })
}

/**
 * 医薬品グループ削除 Mutation
 */
export const useDeleteDrugGroupMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (groupId: string) => {
      await deleteDrugGroup(groupId)
      return groupId
    },
    onSuccess: (groupId) => {
      showToast('医薬品グループを削除しました', 'success')
      // 詳細キャッシュを削除
      queryClient.removeQueries({
        queryKey: queryKeys.drugGroups.detail(groupId),
      })
      // 一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: queryKeys.drugGroups.all,
      })
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const errorMessage =
        error.response?.data?.detail || '医薬品グループの削除に失敗しました'
      showToast(errorMessage, 'error')
    },
  })
}
